<script setup>
import { ref, watch } from 'vue';

const props = defineProps({
  userLocation: {
    type: String,
    default: 'London'
  }
});

const newsItems = ref([]);
const loading = ref(false);
const errorMessage = ref('');

// Helper to clean up HTML entities in titles
const decodeEntities = (html) => {
  const txt = document.createElement('textarea');
  txt.innerHTML = html;
  return txt.value;
};

// Maps location terms to specific regional RSS feeds
const getFeedUrl = (location) => {
  const loc = location.toLowerCase();
  
  if (loc.includes('london')) {
    return 'https://feeds.bbci.co.uk/news/england/london/rss.xml';
  } else if (loc.includes('scotland') || loc.includes('edinburgh') || loc.includes('glasgow')) {
    return 'https://feeds.bbci.co.uk/news/scotland/rss.xml';
  } else if (loc.includes('wales') || loc.includes('cardiff')) {
    return 'https://feeds.bbci.co.uk/news/wales/rss.xml';
  } else if (loc.includes('northern ireland') || loc.includes('belfast')) {
    return 'https://feeds.bbci.co.uk/news/northern_ireland/rss.xml';
  } else if (loc.includes('manchester') || loc.includes('birmingham') || loc.includes('england')) {
    return 'https://feeds.bbci.co.uk/news/england/rss.xml';
  }
  
  // Default to Top UK Headlines
  return 'https://feeds.bbci.co.uk/news/uk/rss.xml';
};

const fetchLocalNews = async () => {
  if (!props.userLocation) return;

  loading.value = true;
  errorMessage.value = '';

  try {
    const rssUrl = getFeedUrl(props.userLocation);
    // Convert public RSS to JSON using rss2json API
    const response = await fetch(`https://api.rss2json.com/v1/api.json?rss_url=${encodeURIComponent(rssUrl)}`);
    const data = await response.json();

    if (data.status !== 'ok' || !data.items) {
      throw new Error('Unable to load news headlines');
    }

    // Take top 3 articles
    newsItems.value = data.items.slice(0, 5).map(item => ({
      title: decodeEntities(item.title),
      link: item.link,
      pubDate: new Date(item.pubDate).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })
    }));
  } catch (err) {
    newsItems.value = [];
    errorMessage.value = 'Failed to load local news';
  } finally {
    loading.value = false;
  }
};

watch(
  () => props.userLocation,
  () => fetchLocalNews(),
  { immediate: true }
);
</script>

<template>
  <div class="news-card">
    <!-- Header -->
    <div class="news-header">
      <i class="bi bi-newspaper news-icon"></i>
      <h2 class="location">Local News</h2>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="state-message">
      Fetching latest news...
    </div>

    <!-- Error State -->
    <div v-else-if="errorMessage" class="error-message">
      {{ errorMessage }}
    </div>

    <!-- News List -->
    <div v-else-if="newsItems.length" class="news-list">
      <a 
        v-for="(item, index) in newsItems" 
        :key="index"
        :href="item.link" 
        target="_blank" 
        rel="noopener noreferrer" 
        class="news-item"
      >
        <p class="news-title">{{ item.title }}</p>
        <span class="news-time">{{ item.pubDate }}</span>
      </a>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@use "@/assets/styles/abstract/variables" as *;
// SCSS Variables
$card-bg: #ffffff;
$primary-text: #0f172a;
$secondary-text: #334155;
$muted-text: #64748b;
$error-color: #e11d48;
$item-bg: #f8fafc;
$item-border: #e2e8f0;
$item-border-hover: #cbd5e1;

/* Card Wrapper - Fixed 370px height + Vertically Centered */
.news-card {
  background: $card-bg;
  padding: 1.5rem;
  border-radius: 16px;
  box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.08);
  width: 100%;
  box-sizing: border-box;
  
  /* Flexbox settings for vertical & horizontal centering */
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  
  text-align: center;
  font-family: system-ui, -apple-system, sans-serif;
  margin: 0 auto;

  /* Header */
  .news-header {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    margin-bottom: 1rem;

    .news-icon {
      font-size: 1.25rem;
      color: $dark-blue;
    }

    .location {
      font-size: 1.2rem;
      color: $secondary-text;
      margin: 0;
      font-weight: 600;
    }
  }

  /* Status Messages */
  .state-message {
    color: $muted-text;
    font-size: 0.95rem;
  }

  .error-message {
    color: $error-color;
    font-size: 0.95rem;
  }

  /* News Article List */
  .news-list {
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 10px;

    .news-item {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      text-align: left;
      background: $item-bg;
      padding: 10px 12px;
      border-radius: 10px;
      border: 1px solid $item-border;
      text-decoration: none;
      transition: transform 0.15s ease, border-color 0.15s ease;

      &:hover {
        border-color: $item-border-hover;
        transform: translateY(-2px);
      }

      .news-title {
        font-size: 0.85rem;
        font-weight: 600;
        color: $primary-text;
        margin: 0 0 4px 0;
        line-height: 1.25;

        /* Clamps long titles to 2 lines maximum */
        display: -webkit-box;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
        overflow: hidden;
      }

      .news-time {
        font-size: 0.7rem;
        color: $muted-text;
        font-weight: 500;
      }
    }
  }
}
</style>